import React from 'react';

interface Props {
  content: string;
}

// Minimal, dependency-free markdown renderer scoped to the structure used by
// our legal documents (headings, paragraphs, bullets, bold/italic, tables,
// hr, blockquotes). Sufficient for click-wrap presentation; not a general-
// purpose md engine.
export const MarkdownView: React.FC<Props> = ({ content }) => {
  const lines = content.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  const renderInline = (text: string): React.ReactNode => {
    // Order matters: bold-italic, bold, italic, code, links.
    const nodes: React.ReactNode[] = [];
    let remaining = text;
    let nKey = 0;
    const patterns: Array<[RegExp, (m: RegExpMatchArray) => React.ReactNode]> = [
      [/^\*\*\*(.+?)\*\*\*/, (m) => <strong key={nKey++}><em>{m[1]}</em></strong>],
      [/^\*\*(.+?)\*\*/, (m) => <strong key={nKey++}>{m[1]}</strong>],
      [/^\*(.+?)\*/, (m) => <em key={nKey++}>{m[1]}</em>],
      [/^`([^`]+)`/, (m) => <code key={nKey++} className="px-1 py-0.5 bg-gray-100 rounded text-[0.85em]">{m[1]}</code>],
      [/^\[([^\]]+)\]\(([^)]+)\)/, (m) => <a key={nKey++} href={m[2]} className="text-green-700 underline" target="_blank" rel="noopener noreferrer">{m[1]}</a>],
    ];
    while (remaining.length > 0) {
      let matched = false;
      for (const [re, render] of patterns) {
        const m = remaining.match(re);
        if (m) {
          nodes.push(render(m));
          remaining = remaining.slice(m[0].length);
          matched = true;
          break;
        }
      }
      if (!matched) {
        const nextSpecial = remaining.search(/[*`[]/);
        if (nextSpecial === -1) {
          nodes.push(remaining);
          remaining = '';
        } else if (nextSpecial === 0) {
          // Lone special char with no matching pattern — emit literally.
          nodes.push(remaining[0]);
          remaining = remaining.slice(1);
        } else {
          nodes.push(remaining.slice(0, nextSpecial));
          remaining = remaining.slice(nextSpecial);
        }
      }
    }
    return <>{nodes}</>;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Horizontal rule
    if (/^---+\s*$/.test(line)) {
      blocks.push(<hr key={key++} className="my-6 border-gray-200" />);
      i++;
      continue;
    }

    // Headings
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const text = h[2].trim();
      const sizes = ['text-2xl', 'text-xl', 'text-lg', 'text-base', 'text-base', 'text-sm'];
      const margins = ['mt-8 mb-4', 'mt-6 mb-3', 'mt-5 mb-2', 'mt-4 mb-2', 'mt-3 mb-2', 'mt-2 mb-1'];
      const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;
      blocks.push(
        React.createElement(
          Tag,
          { key: key++, className: `${sizes[level - 1]} ${margins[level - 1]} font-semibold text-gray-900` },
          renderInline(text)
        )
      );
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s/.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      blocks.push(
        <blockquote key={key++} className="border-l-4 border-green-500 pl-4 my-3 text-gray-700 italic">
          {renderInline(quoteLines.join(' '))}
        </blockquote>
      );
      continue;
    }

    // Tables (markdown pipe syntax)
    if (/^\|.+\|/.test(line) && i + 1 < lines.length && /^\|[\s\-:|]+\|/.test(lines[i + 1])) {
      const headerCells = line.split('|').slice(1, -1).map((c) => c.trim());
      i += 2; // skip separator row
      const rows: string[][] = [];
      while (i < lines.length && /^\|.+\|/.test(lines[i])) {
        rows.push(lines[i].split('|').slice(1, -1).map((c) => c.trim()));
        i++;
      }
      blocks.push(
        <div key={key++} className="my-4 overflow-x-auto">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {headerCells.map((c, idx) => (
                  <th key={idx} className="px-3 py-2 border border-gray-200 text-left font-semibold">
                    {renderInline(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx} className="even:bg-gray-50/50">
                  {row.map((c, cIdx) => (
                    <td key={cIdx} className="px-3 py-2 border border-gray-200 align-top">
                      {renderInline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={key++} className="list-disc pl-6 my-3 space-y-1 text-gray-800">
          {items.map((item, idx) => <li key={idx}>{renderInline(item)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={key++} className="list-decimal pl-6 my-3 space-y-1 text-gray-800">
          {items.map((item, idx) => <li key={idx}>{renderInline(item)}</li>)}
        </ol>
      );
      continue;
    }

    // Code fence — render as preformatted block.
    if (/^```/.test(line)) {
      i++;
      const codeLines: string[] = [];
      while (i < lines.length && !/^```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // closing fence
      blocks.push(
        <pre key={key++} className="bg-gray-100 rounded p-3 my-3 overflow-x-auto text-xs font-mono">
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph (merge consecutive non-empty lines)
    const paraLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,6}\s|>\s|\s*[-*]\s|\s*\d+\.\s|---+\s*$|```|\|.+\|)/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++} className="my-3 leading-relaxed text-gray-800">
        {renderInline(paraLines.join(' '))}
      </p>
    );
  }

  return <div className="prose-legal">{blocks}</div>;
};

export default MarkdownView;
